import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { ArrowLeft, Mail, Phone, MapPin, User, Building2, Package } from "lucide-react";

const typeLabel = (t) => (t === "invoice" ? "Quote" : t === "sales_order" ? "Order" : t);

const InfoBlock = ({ icon: Icon, title, name, email, phone, address, testid }) => {
  if (!name && !email && !phone && !address) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 label-eyebrow mb-2">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      {name && <div className="font-medium text-sm">{name}</div>}
      {address && (
        <div className="text-xs text-muted-foreground mt-1 flex items-start gap-1.5 whitespace-pre-line">
          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
          {address}
        </div>
      )}
      {email && (
        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
          <Mail className="w-3 h-3" />
          {email}
        </div>
      )}
      {phone && (
        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
          <Phone className="w-3 h-3" />
          {phone}
        </div>
      )}
    </div>
  );
};

export default function ApplianceDocumentDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/appliance/documents/${id}`)
      .then(({ data }) => setDoc(data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="p-8">
        <Link to="/appliance/documents" className="text-sm text-primary inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to documents
        </Link>
        <div className="mt-6 text-muted-foreground">Document not found.</div>
      </div>
    );
  }
  if (!doc) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const items = doc.line_items || [];

  return (
    <div className="p-6 lg:p-10 max-w-[1100px] animate-fade-in" data-testid="appliance-document-detail">
      <Link
        to="/appliance/documents"
        className="text-sm text-primary inline-flex items-center gap-1 mb-6"
        data-testid="appliance-doc-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back to documents
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <span
              className={`text-xs px-2 py-0.5 rounded-full border ${
                doc.document_type === "invoice"
                  ? "border-[#1d4ed8]/30 text-[#1d4ed8] bg-[#1d4ed8]/5"
                  : "border-[#0f766e]/30 text-[#0f766e] bg-[#0f766e]/5"
              }`}
            >
              {typeLabel(doc.document_type)}
            </span>
            {doc.notes && <span className="text-xs text-muted-foreground">{doc.notes}</span>}
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-2 font-mono">#{doc.document_number}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Issued {doc.issue_date || "—"}
            {doc.due_date ? ` · Due ${doc.due_date}` : ""}
            {doc.prepared_by ? ` · By ${doc.prepared_by}` : ""}
          </p>
        </div>
        <div className="text-right">
          <div className="label-eyebrow">Total</div>
          <div className="font-heading text-3xl font-bold num">{fmtCurrency(doc.total)}</div>
          <div className="text-xs text-muted-foreground">{doc.currency || "USD"}</div>
        </div>
      </div>

      {/* Parties */}
      <div className="surface-card p-6 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
        <InfoBlock
          icon={User}
          title="Customer"
          name={doc.customer}
          email={doc.customer_email}
          phone={doc.customer_phone}
          address={doc.customer_address}
          testid="appliance-doc-customer"
        />
        <InfoBlock
          icon={Building2}
          title="Dealer"
          name={doc.dealer_name}
          email={doc.dealer_email}
          phone={doc.dealer_phone}
          address={doc.dealer_address}
          testid="appliance-doc-dealer"
        />
        <InfoBlock
          icon={Building2}
          title="Vendor"
          name={doc.vendor}
          address={doc.vendor_address}
          testid="appliance-doc-vendor"
        />
      </div>

      {/* Line items */}
      <div className="surface-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border/70 flex items-center gap-2">
          <Package className="w-4 h-4 text-primary" />
          <h2 className="font-heading text-lg font-semibold">Line items ({items.length})</h2>
        </div>
        <div className="divide-y divide-border/70">
          {items.map((it, i) => {
            const qty = Number(it.qty) || 0;
            const price = Number(it.unit_price) || 0;
            return (
              <div key={i} className="flex items-center gap-4 px-6 py-4" data-testid={`appliance-line-item-${i}`}>
                <div className="w-14 h-14 rounded-md bg-secondary overflow-hidden shrink-0 grid place-items-center">
                  {it.image_url ? (
                    <img
                      src={it.image_url}
                      alt={it.title || it.description}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <Package className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{it.title || it.description || "Item"}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[it.description !== it.title ? it.description : null, it.model, it.dimensions]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="num text-sm font-semibold">{fmtCurrency(qty * price)}</div>
                  <div className="text-xs text-muted-foreground num">
                    {qty} × {fmtCurrency(price)}
                  </div>
                </div>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="py-10 text-center text-muted-foreground text-sm">No line items on this document.</div>
          )}
        </div>

        {/* Totals */}
        <div className="px-6 py-4 border-t border-border/70 bg-secondary/30">
          <div className="ml-auto max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="num">{fmtCurrency(doc.subtotal)}</span>
            </div>
            {Number(doc.delivery_cost) > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delivery</span>
                <span className="num">{fmtCurrency(doc.delivery_cost)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-border/60 pt-1.5 font-bold">
              <span>Total</span>
              <span className="num">{fmtCurrency(doc.total)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground mt-4">
        Synced from Base44 · created {fmtDate(doc.created_date)}
      </div>
    </div>
  );
}
